/**
 * Next.js-safe Link component with Dashboard3 styling
 *
 * Automatically handles internal vs external links with proper security attributes
 *
 * @example
 * ```tsx
 * <Link href="/dashboard">Go to Dashboard</Link>
 * <Link href="https://example.com" external>External Site</Link>
 * ```
 */

import {
  Link as ChakraLink,
  type LinkProps as ChakraLinkProps,
} from "@chakra-ui/react";
import NextLink from "next/link";
import { forwardRef } from "react";

export interface LinkProps extends ChakraLinkProps {
  /**
   * Link URL
   */
  href: string;
  /**
   * Whether this is an external link (adds rel="noopener noreferrer" and target="_blank")
   */
  external?: boolean;
  /**
   * Aria label for accessibility
   */
  "aria-label"?: string;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    { href, external = false, children, "aria-label": ariaLabel, ...props },
    ref,
  ) => {
    // Auto-detect external links if not explicitly set
    const isExternal =
      external ||
      href.startsWith("http://") ||
        href.startsWith("https://") ||
        href.startsWith("mailto:") ||
      href.startsWith("tel:");

    if (isExternal) {
      return (
        <ChakraLink
          ref={ref}
          href={href}
          isExternal
          rel="noopener noreferrer"
          target="_blank"
          aria-label={ariaLabel}
          {...props}
        >
          {children}
        </ChakraLink>
      );
    }

    return (
      <ChakraLink
        ref={ref}
        as={NextLink}
        href={href}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </ChakraLink>
    );
  },
);

Link.displayName = "Link";
